'use client';

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import gsap from 'gsap';
import { Text, useTranslation, useUi } from '@weareconceptstudio/core';

//* Helpers
import { config } from '@/helpers';

//* Styles
import HeaderStyle from './style';

//* Components
import Logo from './Logo';
import MenuBar from './MenuBar';
import Button from '../Button';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
	const { winWidth, isBurgerMenuOpen, toggleBurgerMenu, closeBurgerMenu } = useUi();

	//! Refs
	const headerRef = useRef();
	const oldScroll = useRef(0);

	//! States
	const [show, setShow] = useState(true);
	const [isHeaderHaveBackground, setIsHeaderHaveBackground] = useState(false);

	const pathname = usePathname();
	const { selectedLang } = useTranslation();

	const cleanedPathname = useMemo(() => {
		return selectedLang ? pathname.replaceAll(`/${selectedLang}`, '') : pathname;
	}, [pathname, selectedLang]);

	//! Mobile Size
	const mobileSize = 1024;

	//! Check Recipes Individual Page
	const isRecipesIndividual = useMemo(() => {
		return cleanedPathname.includes(config.routes.recipes.path) && cleanedPathname.split('/').length >= 4;
	}, [pathname]);

	//! Header Background checking for pages
	const hasDefaultHeaderBg = Object.values(config.routes).some((route) => route.isDefaultHeaderBackground && cleanedPathname.includes(route.path) && !isRecipesIndividual);

	useEffect(() => {
		if (isBurgerMenuOpen && winWidth >= mobileSize) {
			closeBurgerMenu();
		}
	}, [winWidth, isBurgerMenuOpen]);

	useEffect(() => {
		closeBurgerMenu();
	}, [pathname, selectedLang]);

	const onScroll = useCallback(() => {
		if (window.scrollY > oldScroll.current && window.scrollY > 0 && !isBurgerMenuOpen) {
			setShow(false);
		} else if (window.scrollY < oldScroll.current || window.scrollY <= 0 || isBurgerMenuOpen) {
			setShow(true);
		}

		window.scrollY > headerRef.current?.offsetHeight ? setIsHeaderHaveBackground(true) : setIsHeaderHaveBackground(false);

		oldScroll.current = window.scrollY;
	}, [isBurgerMenuOpen, pathname, selectedLang, isHeaderHaveBackground]);

	useEffect(() => {
		isBurgerMenuOpen ? document.body.classList.add('hidden') : document.body.classList.remove('hidden');
	}, [isBurgerMenuOpen, pathname, selectedLang]);

	useEffect(() => {
		window.addEventListener('scroll', onScroll);

		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, [isBurgerMenuOpen, pathname, selectedLang]);

	useEffect(() => {
		onScroll();
	}, [pathname, selectedLang, onScroll]);

	//! Animations
	useEffect(() => {
		gsap.killTweensOf(headerRef.current);

		show && gsap.set(headerRef.current, { display: 'block' });

		gsap.to(headerRef.current, {
			duration: window.scrollY === 0 ? 0.7 : 0.3,
			y: show ? '0%' : '-100%',
		});

		// .then(() => gsap.set(headerRef.current, { display: !show ? 'none' : 'block' }));
	}, [show]);

	useEffect(() => {
		if (!isBurgerMenuOpen || !headerRef.current) return;

		const logo = headerRef.current.querySelector('.logo-block');
		const langSwitcher = headerRef.current.querySelector('.languages-switcher-wrapper');
		const burgerToggle = headerRef.current.querySelector('.burger-open-close');
		const menuBarChildren = headerRef.current.querySelector('.menu-bar-block');
		const burgerMenuBtn = headerRef.current.querySelector('.btn');

		if (!menuBarChildren || !burgerMenuBtn) return;

		const menuItems = Array.from(menuBarChildren.children);

		if (isBurgerMenuOpen) {
			gsap.fromTo(burgerToggle, { opacity: 0 }, { opacity: 1, duration: 0.5, ease: 'power1.in' });
			gsap.fromTo([...menuItems], { opacity: 0 }, { opacity: 1, delay: 0.3, duration: 0.5, stagger: 0.03, ease: 'power1.in' });

			setTimeout(() => {
				if (langSwitcher) {
					gsap.fromTo(langSwitcher, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power1.in' });
				}
			}, 180);

			setTimeout(() => {
				if (logo) {
					gsap.fromTo(logo, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power1.in' });
				}
			}, 380);

			gsap.fromTo(
				burgerMenuBtn,
				{ scale: 0 },
				{
					scale: 1,
					delay: (2 + menuItems.length) * 0.1,
					duration: 0.6,
					ease: 'back.out',
				}
			);
		}
	}, [isBurgerMenuOpen]);

	useEffect(() => {
		if (!hasDefaultHeaderBg) {
			if (isBurgerMenuOpen) {
				setTimeout(() => {
					document.body.classList.add('external');
				}, 380);
			} else {
				setTimeout(() => {
					document.body.classList.remove('external');
				}, 100);
			}
		} else {
			document.body.classList.remove('external');
		}
	}, [isBurgerMenuOpen, hasDefaultHeaderBg]);

	return (
		<HeaderStyle
			ref={headerRef}
			className={`header-container ${isBurgerMenuOpen ? 'burger-shape' : ''} ${hasDefaultHeaderBg || isHeaderHaveBackground ? 'default-background' : ''}`}
		>
			{winWidth < mobileSize ? <div className='shape-burger-wrapper' /> : null}

			<div className='header-inner-wrapper'>
				<Logo />

				<MenuBar className={`${isBurgerMenuOpen ? 'burger-version' : ''}`} />

				<div className='right-block'>
					<LanguageSwitcher />

					{winWidth >= mobileSize ? (
						<Button
							url={config.routes.contactUs.path}
							text={config.routes.contactUs.name}
							btnType={'white-type'}
						/>
					) : (
						<Text
							onClick={() => toggleBurgerMenu()}
							text={isBurgerMenuOpen ? 'close' : 'menu'}
							className={`p1 light-gray-1000 font-fss-regular burger-open-close cursor-pointer`}
						/>
					)}
				</div>
			</div>
		</HeaderStyle>
	);
};

export default Header;
